import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import {
  tmsApi,
  type OrderChargeCost,
  type ReplaceOrderChargeCostsRequest,
} from '../client';

/**
 * Stable query-key factory for order charge/cost caches. Lines are scoped to
 * an order — that's both the natural cache key and the BE list filter.
 */
export const orderChargeCostKeys = {
  all: ['tms', 'orderChargeCost'] as const,
  byOrder: (orderId: string) => ['tms', 'orderChargeCost', 'order', orderId] as const,
};

/**
 * Fetch an order's charge/cost lines (ordered side then sequence, BE-side).
 * Pass `null` to keep the query disabled (e.g. on a not-yet-saved order).
 */
export function useOrderChargeCosts(
  orderId: string | null,
  options?: Omit<UseQueryOptions<OrderChargeCost[], Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<OrderChargeCost[], Error>({
    queryKey: orderId ? orderChargeCostKeys.byOrder(orderId) : orderChargeCostKeys.all,
    queryFn: () => {
      if (!orderId) throw new Error('No order id');
      return tmsApi.getOrderChargeCosts(orderId);
    },
    enabled: !!orderId,
    staleTime: 30 * 1000,
    ...options,
  });
}

/**
 * PUT the full charge/cost set for an order (atomic replace). Invalidates the
 * per-order cache so the saved + re-snapshotted rows (server ids, locked
 * descriptions) flow back into the panel.
 */
export function useReplaceOrderChargeCosts() {
  const qc = useQueryClient();
  return useMutation<OrderChargeCost[], Error, ReplaceOrderChargeCostsRequest>({
    mutationKey: [...orderChargeCostKeys.all, 'replace'],
    mutationFn: (body) => tmsApi.replaceOrderChargeCosts(body),
    onSuccess: (_rows, { orderId }) => {
      qc.invalidateQueries({ queryKey: orderChargeCostKeys.byOrder(orderId) });
    },
  });
}
